import { AxisComponent, ChartOptions, LegendComponent, TooltipComponent, Transform, ViewComposition } from '@antv/g2';

class BaseChartOptions {
    options: ChartOptions = {};

    constructor(data: Array<any>) {
        this.options = {
            type: 'view',
            autoFit: true,
            data: data,
            children: [],
        };
    }

    checkChildren() {
        if (this.options && this.options.children && this.options.children.length) return true;

        return false;
    }

    setChildren(children : ChildrenType){
        this.options.children?.push(...children);
    }

    setChildData(id: string, data: Array<any>) {
        if (!this.checkChildren()) return;
        const child = this.options.children!.find((child : any) => child.id === id);
        if (child) {
            child.data = data;
        }
    }

    setStyle(id: string, style: Record<string,any>) {
        if (!this.checkChildren()) return;
        const child = this.options.children!.find((child : any) => child.id === id);
        if (child) {
            child.style = style;
        }
    }

    // Method to set axis
    setAxis(id: string, axis: AxisConfig) {
        if (!this.checkChildren()) return;
        const child = this.options.children!.find((child : any) => child.id === id);
        if (child) {
            child.axis = axis;
        }
    }

    // Method to set scale
    setScale(id: string, scale: Record<string,any>) {
        if (!this.checkChildren()) return;
        const child = this.options.children!.find((child : any) => child.id === id);
        if (child) {
            child.scale = scale;
        }
    }

     // Method to set encode
     setEncoding(id: string, encode: Encode) {
        if (!this.checkChildren()) return;
        const child = this.options.children!.find((child : any) => child.id === id);
        if (child) {
            child.encode = encode;
        }
    }

    setTooltip(id: string, tooltip:TooltipComponent){
        if (!this.checkChildren()) return;
        const child = this.options.children!.find((child : any) => child.id === id);
        if (child) {
            child.tooltip = tooltip;
        }
    }

    setTransform(id: string, transform:Transform | Transform[]){
        if (!this.checkChildren()) return;
        const child = this.options.children!.find((child : any) => child.id === id);
        if (child) {
            child.transform = transform;
        }
    }

    setDataLabel(id: string, labels:any){
        if (!this.checkChildren()) return;
        const child = this.options.children!.find((child : any) => child.id === id);
        if (child) {
            child.labels = labels;
        }
    }

    setInteraction(id: string, interaction:any){
        if (!this.checkChildren()) return;
        const child = this.options.children!.find((child : any) => child.id === id);
        if (child) {
            child.interaction = interaction;
        }
    }

    setAdditionalProps(id: string, additionalProps : Record<string,any>){
        if (!this.checkChildren()) return;
        const child = this.options.children!.find((child : any) => child.id === id);
        if (child) {
            const propList = Object.keys(additionalProps);
            propList.map((prop)=>{
                //@ts-ignore
                child[prop] = additionalProps[prop];
            })
        }
    }

    setViewAdditionalProps(additionalProps: Partial<Record<ChartOptionsKeys,any>>){
        if (additionalProps) {
            const propList = Object.keys(additionalProps) as ChartOptionsKeys[];
            propList.map((prop)=>{
                this.options[prop]= additionalProps[prop];
            })
        }
    }


    setAttributes(attributes: Attributes) {
        this.options= {...this.options, ...attributes};
    }

    getOptions() {
        return this.options;
    }
}

 // Function to set configurations
export const applyConfig = (chartOptions: BaseChartOptions, config: ChartConfig, type: string) => {
    chartOptions.setEncoding(type, config.encode);
    if(config.data && config.data?.length)chartOptions.setChildData(type, config.data);
    if (config.style) chartOptions.setStyle(type, config.style);
    if (config.labels) chartOptions.setDataLabel(type, config.labels);
    if (config.axis) chartOptions.setAxis(type, config.axis);
    if (config.transform) chartOptions.setTransform(type, config.transform);
    if (config.interaction) chartOptions.setInteraction(type, config.interaction);
    if (config.scale) chartOptions.setScale(type, config.scale);
    if (config.tooltip !== undefined) chartOptions.setTooltip(type, config.tooltip);
    if (config.additionalConfig) chartOptions.setAdditionalProps(type, config.additionalConfig);
};

export default BaseChartOptions;

export type ChartConfig = {
    encode: Encode;
    data?:Array<Record<string,any>>;
    axis?: AxisConfig;
    style?: Record<string, any>;
    scale?: Record<string, any>;
    labels?: Record<string, any>[];
    tooltip?: TooltipComponent;
    transform?: Transform | Transform[];
    interaction?: Record<string, any>;
    additionalConfig?: Record<string, any>;
};


type AttributesType =
    | 'height'
    | 'width'
    | 'margin'
    | 'marginLeft'
    | 'marginTop'
    | 'marginRight'
    | 'marginBottom'
    | 'padding'
    | 'paddingLeft'
    | 'paddingTop'
    | 'paddingRight'
    | 'paddingBottom'
    | 'inset'
    | 'insetLeft'
    | 'insetTop'
    | 'insetRight'
    | 'insetBottom';

type Attributes = Pick<ViewComposition, AttributesType>;

type ChildrenType = any[];

type AxisConfig = {
    x?: Record<string, any>;
    y?: Record<string, any>;
};

// type NeverType<Type> = {
//     [Property in keyof Type]?: never;
// };

type BaseEncode = {
    x: string | Function;
    y: string | Function;
    [key: string]: any;
};

type Encode = BaseEncode ;

type ChartOptionsKeys = keyof ChartOptions;

export type ChartCommonProps = {
    className?: string;
    data: Array<Record<string,any>>;
    sizeAttributes?: Attributes;
    viewLevelAdditionalProps?: Partial<Record<ChartOptionsKeys, any>>;
    additionalMarks ?: (any | AxisComponent | LegendComponent)[];
    "data-test" ?: string;
};
